import {type ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PillButton} from '@Components/pill-button';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {isUserImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {isDesignAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {PillButtonSize} from '@/components/pill-button/pill-button.types';
import {IconSize} from '@/components/icon-v2/icon.types';
import styles from './ai-image-prompt-box.module.scss';
import {
  AI_IMAGE_ATTACHMENT_DROPDOWN_OPTIONS,
  AiImageAspectRatioOptions,
  AiImageFilterOptions,
  AiImageModelOption,
  AiImageStylesOptions,
  DEFAULT_SELECTED_AI_IMAGE_MODEL,
  MediaTypePrompt,
} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {
  type AiImageReferenceImage,
  AiImageReferenceImageType,
  type AITextToImageBackendData,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  type GenerateAIImageResponse,
  generateImage,
  type GenerateImageParams,
  getRemainingAiUsages,
} from '@Libraries/pmw-stock-media-library';
import {
  getAiImageModelOptionFromMenuValue,
  getContextualMenuGroups,
  getCreditCostForModel,
  getExpectedAiImageCount,
  getMaxReferenceImagesForModel,
  getPlaceHolderText,
  getPromptBoxOptions,
  getAiImageGenerationErrorCode,
} from '@Panels/ai-image-panel/ai-image-panel-library';
import {isUserPremium} from '@Libraries/user.library';
import {showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {ErrorType} from '@Components/ai-prompt-box/components/ai-prompt-box-issue/ai-prompt-box-issue.types';
import {getErrorMessage} from '@Utils/error.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateSelectedAiImageModelId, updateSelectedAiImageModelIdBackend} from '@/reducers/user-preferences.reducer';
import {v4 as uuidv4} from 'uuid';
import {AiImageGenerationFailureStatus} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {addAiImageGenerationFailure, removeAiImageGenerationFailure, selectPendingAiImageGenerationRetry} from '@Components/poster-editor/poster-editor-reducer';
import {AiImageFailedGenerationsList} from '@Panels/ai-image-panel/components/ai-image-failed-generations-list';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize, TextType} from '@Components/text';
import {capitalize} from '@Utils/string.util';
import {AI_IMAGE_PROMPT_BOX_ID, AI_IMAGE_SETTINGS_POPUP_MAX_WIDTH, AI_IMAGE_SUBMIT_LOADING_PULSE_MS} from './ai-image-prompt-box.types';

interface AiImagePromptBoxProps {
  onImageGenerationStarted: (generatingImagesAspectRatio: AiImageAspectRatioOptions, expectedImageCount: number) => void;
  onImageGenerationSuccessful: (images: AITextToImageBackendData[]) => void;
  onImageGenerationFailed: () => void;
  mediaType?: MediaTypePrompt;
  alignSettingsPopupWithFooter?: boolean;
}

export default function AiImagePromptBox({...props}: AiImagePromptBoxProps): ReactElement<HTMLDivElement> {
  const [mediaTypePrompt, setMediaTypePrompt] = useState<MediaTypePrompt>(props.mediaType ?? MediaTypePrompt.PHOTO);
  const promptRowRef = useRef<HTMLDivElement>(null);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const dispatch = useAppDispatch();
  const selectedAiImageModelId = useAppSelector((state) => state.userPreferences.selectedAiImageModelId);
  const {actions, state, selectedContextualMenuItems} = useAiPromptBox(
    AI_IMAGE_PROMPT_BOX_ID,
    getPromptBoxOptions(mediaTypePrompt, selectedAiImageModelId ?? DEFAULT_SELECTED_AI_IMAGE_MODEL, AiImageStylesOptions.Photographic, AiImageAspectRatioOptions.Square)
  );
  const selectedModel = getAiImageModelOptionFromMenuValue(selectedContextualMenuItems[AiImageFilterOptions.AiModel]?.[0]);
  const selectedStyle = selectedContextualMenuItems[AiImageFilterOptions.Styles][0] as AiImageStylesOptions;
  const selectedAspectRatio = selectedContextualMenuItems[AiImageFilterOptions.AspectRatio][0] as AiImageAspectRatioOptions;
  const attachmentCount = state.attachments?.items.length ?? 0;
  const pendingRetry = useAppSelector(selectPendingAiImageGenerationRetry);

  useEffect(() => {
    getRemainingTextToImageInvocations();
  }, []);

  useEffect(() => {
    actions.update({
      promptBoxState: PromptBoxState.ACTIVE,
    });
  }, []);

  useEffect(() => {
    if (props.mediaType) {
      setMediaTypePrompt(props.mediaType);
    }
  }, [props.mediaType]);

  useEffect(() => {
    onSelectedModelUpdate();
  }, [selectedModel]);

  useEffect(() => {
    actions.setActiveIfNotProcessing();
  }, [selectedModel, selectedStyle, selectedAspectRatio, mediaTypePrompt]);

  useEffect(() => {
    actions.update({
      contextualMenu: getContextualMenuGroups(selectedModel, selectedStyle, selectedAspectRatio, mediaTypePrompt, attachmentCount),
    });
  }, [attachmentCount]);

  useEffect(() => {
    if (!pendingRetry) {
      return;
    }
    dispatch(removeAiImageGenerationFailure(pendingRetry.id));
    void runGeneration(pendingRetry.params, pendingRetry.aspectRatio);
  }, [pendingRetry, dispatch]);

  useEffect(() => {
    const pillVisibility = [PromptBoxState.ACTIVE, PromptBoxState.LOADING].includes(state.promptBoxState);
    if (pillVisibility !== state.creditsPill?.visible) {
      actions.update({
        creditsPill: {
          visible: pillVisibility,
          actionCost: state.creditsPill?.actionCost,
          showPremiumIcon: !isUserPremium(),
          remainingCredits: state.creditsPill?.remainingCredits,
        },
      });
    }
  }, [state.promptBoxState]);

  const getRemainingTextToImageInvocations = (): void => {
    getRemainingAiUsages()
      .then((remainingCredits) => {
        actions.update({
          creditsPill: {
            remainingCredits: remainingCredits,
          },
        });
      })
      .catch((e: unknown) => {
        window.PMW.log(`AI image prompt box: failed to fetch remaining AI credits: ${getErrorMessage(e)}`);
      });
  };

  const updateSelectedMediaType = (mediaType: MediaTypePrompt): void => {
    setMediaTypePrompt(mediaType);
    const style = mediaType === MediaTypePrompt.STICKER ? AiImageStylesOptions.DigitalArt : AiImageStylesOptions.Photographic;
    const aspectRatio = mediaType === MediaTypePrompt.PHOTO ? selectedAspectRatio : AiImageAspectRatioOptions.Square;
    actions.update({
      contextualMenu: getContextualMenuGroups(selectedModel, style, aspectRatio, mediaType, attachmentCount),
      placeholder: getPlaceHolderText(mediaType),
    });
  };

  const handleInsufficientCreditsError = (): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            void onGenerate();
          }
        },
        noop,
        window.i18next.t('pmwjs_purchase_more_credits_continue_using_ai_images_bg_remover_and_more'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const updateUserModelPreference = (model: AiImageModelOption): void => {
    dispatch(updateSelectedAiImageModelId(model));
    void updateSelectedAiImageModelIdBackend(model);
  };

  const onSelectedModelUpdate = (): void => {
    updateActionCost(getCreditCostForModel(selectedModel));
    const maxAttachments = getMaxReferenceImagesForModel(selectedModel);
    actions.update({
      attachments: {
        items: (state.attachments?.items ?? []).slice(0, maxAttachments),
        maxAttachments,
        dropdown: AI_IMAGE_ATTACHMENT_DROPDOWN_OPTIONS,
      },
    });
    if (selectedModel !== (selectedAiImageModelId ?? DEFAULT_SELECTED_AI_IMAGE_MODEL)) {
      updateUserModelPreference(selectedModel);
    }
  };

  const updateActionCost = (actionCost: number): void => {
    if (actionCost !== state.creditsPill?.actionCost) {
      actions.update({
        creditsPill: {
          visible: state.creditsPill?.visible ?? false,
          actionCost: actionCost,
          showPremiumIcon: !isUserPremium(),
          remainingCredits: state.creditsPill?.remainingCredits ?? 0,
        },
      });
    }
  };

  const getReferenceImageType = (attachment: UserImageAttachment): AiImageReferenceImageType => {
    return attachment.type === PromptAttachmentType.REFERENCE_IMAGE ? AiImageReferenceImageType.AI_ATTACHMENT : AiImageReferenceImageType.USER_IMAGE;
  };

  const getUploadedReferenceImages = (): AiImageReferenceImage[] => {
    return (state.attachments?.items ?? [])
      .filter(isUserImageAttachment)
      .filter((attachment) => attachment.hashedFilename !== '')
      .map((attachment) => ({hashedFilename: attachment.hashedFilename, type: getReferenceImageType(attachment)}));
  };

  const getDesignReferenceImages = (): AiImageReferenceImage[] => {
    return (state.attachments?.items ?? [])
      .filter(isDesignAttachment)
      .filter((attachment) => attachment.type === PromptAttachmentType.DESIGN)
      .map((attachment) => ({hashedFilename: attachment.designId, pageHashedId: attachment.pageHashedId, type: AiImageReferenceImageType.DESIGN}));
  };

  const getReferenceImages = (): AiImageReferenceImage[] => {
    return [...getUploadedReferenceImages(), ...getDesignReferenceImages()];
  };

  const promptBoxUploadsOption: FooterLeftAction = useCallback(
    (footerProps: FooterLeftActionProps): ReactElement | null => (
      <AiPromptBoxUploads {...footerProps} renderAttachmentTooltip defaultTooltipText={window.i18next.t('pmwjs_add_reference_images')} />
    ),
    []
  );

  const promptBoxOptions = useMemo(() => [promptBoxUploadsOption], [promptBoxUploadsOption]);

  const onGenerateImageSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    props.onImageGenerationSuccessful(generateImagesResponse.generatedImages);
    actions.update({
      creditsPill: {
        visible: state.creditsPill?.visible ?? false,
        actionCost: getCreditCostForModel(selectedModel),
        showPremiumIcon: !isUserPremium(),
        remainingCredits: generateImagesResponse.remainingCredits,
      },
    });
  };

  const showGenerationError = (message: string): void => {
    actions.update({
      error: {
        type: ErrorType.ERROR,
        message: message,
        icon: 'icon-warning',
      },
    });
  };

  const onGenerate = async (): Promise<void> => {
    if (!isUserPremium()) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (!state.prompt || state.prompt.trim().length === 0) {
      return;
    }

    const referenceImages = getReferenceImages();
    if (referenceImages.length > getMaxReferenceImagesForModel(selectedModel)) {
      showGenerationError(window.i18next.t('pmwjs_seedream_supports_single_reference_image'));
      return;
    }

    await runGeneration(
      {
        prompt: state.prompt,
        style: selectedStyle,
        aspectRatio: selectedAspectRatio,
        category: mediaTypePrompt,
        providerId: selectedModel === AiImageModelOption.Auto ? undefined : selectedModel,
        referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
      },
      selectedAspectRatio
    );
  };

  const runGeneration = async (generateImageParams: GenerateImageParams, aspectRatio: AiImageAspectRatioOptions): Promise<void> => {
    actions.update({
      error: null,
      promptBoxState: PromptBoxState.LOADING,
    });
    let hasShownSkeletons = false;
    const showSkeletonsTimer = window.setTimeout(() => {
      hasShownSkeletons = true;
      actions.update({promptBoxState: PromptBoxState.ACTIVE});
      const expectedImageCount = getExpectedAiImageCount(generateImageParams.referenceImages?.length ?? 0);
      props.onImageGenerationStarted(aspectRatio, expectedImageCount);
    }, AI_IMAGE_SUBMIT_LOADING_PULSE_MS);

    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_GENERATE, {
      [GA4EventParamName.TYPE]: generateImageParams.style ?? selectedStyle,
    });

    try {
      const generateImageResponse = await generateImage(generateImageParams);
      onGenerateImageSuccess(generateImageResponse);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : '';

      if (errorMessage.includes(GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE)) {
        handleInsufficientCreditsError();
      } else {
        const errorCode = getAiImageGenerationErrorCode(errorMessage);
        if (errorCode === undefined) {
          window.PMW.log(`AI image prompt box: unexpected error during image generation: ${getErrorMessage(e)}`);
        }
        dispatch(
          addAiImageGenerationFailure({
            id: uuidv4(),
            status: AiImageGenerationFailureStatus.ERROR,
            aspectRatio,
            params: generateImageParams,
            errorCode,
          })
        );
      }

      if (hasShownSkeletons) {
        props.onImageGenerationFailed();

        return;
      }
      window.clearTimeout(showSkeletonsTimer);
      actions.update({promptBoxState: PromptBoxState.ACTIVE});
    }
  };

  const getAiPromptBox = (): ReactElement<HTMLDivElement> => {
    return (
      <AiPromptBox
        containerClasses={styles.promptBoxContainer}
        id={AI_IMAGE_PROMPT_BOX_ID}
        data-testid={PosterEditorTestId.AI_IMAGE_PROMPT_BOX}
        inputTestId={PosterEditorTestId.AI_IMAGE_PROMPT_INPUT}
        submitTestId={PosterEditorTestId.AI_IMAGE_PROMPT_SUBMIT}
        initialConfig={getPromptBoxOptions(mediaTypePrompt, selectedModel, selectedStyle, selectedAspectRatio)}
        onGenerate={onGenerate}
        onRegenerate={onGenerate}
        onStop={(): Promise<void> => Promise.resolve()}
        options={promptBoxOptions}
        allowSoftSendDuringUploads={false}
      />
    );
  };

  const getSettingsPillSelector = (): ReactElement => {
    return (
      <PillButton
        size={PillButtonSize.SMALL}
        text={window.i18next.t('pmwjs_settings')}
        leftIconProps={{
          icon: 'icon-filter-1',
          size: IconSize.SIZE_ICON_16,
        }}
        customContent={
          <>
            <Text disableSelection bold val={window.i18next.t('pmwjs_settings')} size={TextSize.XSMALL} type={TextType.BODY} className="content-body" />
            <Icon size={IconSize.SIZE_ICON_16} icon={isSettingsDropdownOpen ? 'icon-caret-up' : 'icon-caret-down'} className="content-body" />
          </>
        }
        onClick={noop}
      />
    );
  };

  const getSettingsDropdown = (): ReactElement => {
    return (
      <>
        <div className={styles.pillRowDivider} />
        <AiPromptBoxContextualMenu
          promptBoxId={AI_IMAGE_PROMPT_BOX_ID}
          footerRef={promptRowRef}
          selector={getSettingsPillSelector()}
          alignPopupWithFooter={props.alignSettingsPopupWithFooter}
          maxContextualMenuWidth={AI_IMAGE_SETTINGS_POPUP_MAX_WIDTH}
          onOpenStateChange={setIsSettingsDropdownOpen}
          modalShowBackButton={false}
        />
      </>
    );
  };

  const getMediaTypePill = (type: MediaTypePrompt, text: string, icon: string): ReactElement => {
    return (
      <PillButton
        isSelected={mediaTypePrompt === type}
        size={PillButtonSize.SMALL}
        leftIconProps={{
          icon,
          size: IconSize.SIZE_ICON_16,
        }}
        text={text}
        onClick={(_) => {
          updateSelectedMediaType(type);
        }}
      />
    );
  };

  const getAdditionalPromptRow = (): ReactElement<HTMLDivElement> => {
    return (
      <div ref={promptRowRef} className={`flex-row-align-center spacing-p-t-3 ${styles.promptPills}`}>
        {getMediaTypePill(MediaTypePrompt.PHOTO, window.i18next.t('pmwjs_photo'), 'icon-image')}
        {getMediaTypePill(MediaTypePrompt.LOGO, capitalize(window.i18next.t('pmwjs_logo')), 'icon-logo')}
        {getMediaTypePill(MediaTypePrompt.TEXT_ART, window.i18next.t('pmwjs_text_art'), 'icon-text-art')}
        {getMediaTypePill(MediaTypePrompt.STICKER, window.i18next.t('pmwjs_sticker'), 'icon-sticker')}
        {getSettingsDropdown()}
      </div>
    );
  };

  return (
    <div className={'_position-relative ct-neutral-primary'}>
      {getAiPromptBox()}
      <AiImageFailedGenerationsList />
      {getAdditionalPromptRow()}
    </div>
  );
}
